import type {
  FetchGroupsParams,
  FetchGroupsResponse,
} from '@e-learning/shared';

/** Cliente mínimo necessário para as chamadas (get, post, put, delete). Compatível com ApiClient do core. */
export interface GroupsApiClient {
  get<T>(url: string): Promise<T>;
  post<T>(url: string, data?: unknown): Promise<T>;
  put<T>(url: string, data?: unknown): Promise<T>;
  delete<T>(url: string): Promise<T>;
}

const USER_GROUP_BASE_PATH = 'v1/UserGroup';
const USER_GROUP_PAGINATION_PATH = 'v1/UserGroup/pagination';
const USER_PAGINATION_PATH = 'v1/User/pagination';

/** Body do POST v1/UserGroup (criar grupo). */
export interface CreateUserGroupRequest {
  name: string;
  description: string;
}

/** Resposta do POST v1/UserGroup. */
export interface CreateUserGroupResponse {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  users: unknown;
}

/** Item de usuário no detalhe do grupo (GET v1/UserGroup/{id}). */
export interface UserGroupDetailUserItem {
  userId: string;
  name: string;
}

/** Resposta do GET v1/UserGroup/{id} (detalhe do grupo para edição). */
export interface UserGroupDetail {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  users: UserGroupDetailUserItem[];
}

/** Body do PUT v1/UserGroup/{id} (atualizar grupo). */
export interface UpdateUserGroupRequest {
  name: string;
  description: string;
  isActive: boolean;
}

/** Body do POST v1/UserGroup/groups/{groupId}/users (adicionar usuários ao grupo). */
export interface AddUsersToGroupRequest {
  userIds: string[];
}

/** Parâmetros para listagem de usuários na seleção do modal de grupos (pageSize fixo 3). */
export interface FetchUsersForGroupSelectionParams {
  page: number;
  search?: string;
}

/** Item de usuário normalizado para o autocomplete de seleção. */
export interface UserForGroupSelectionItem {
  id: string;
  name: string;
  email: string;
}

/** Resposta da listagem de usuários para seleção em grupos. */
export interface FetchUsersForGroupSelectionResponse {
  data: UserForGroupSelectionItem[];
  total: number;
}

/** Item de usuário retornado pela API v1/User/pagination */
interface UserPaginationApiItem {
  id: string;
  name: string | null;
  email: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string;
  roles: Array<{ id: string; name: string }>;
  userGroups: Array<{ id: string; name?: string }>;
}

interface UserPaginationApiResponse {
  users: UserPaginationApiItem[];
  totalRecords: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

/** Item de grupo retornado pela API v1/UserGroup/pagination */
interface UserGroupPaginationApiItem {
  id: string;
  name: string;
  assignedCourses: number;
  qtdUsers: number;
}

interface UserGroupPaginationApiResponse {
  userGroups: UserGroupPaginationApiItem[];
  totalRecords: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

/** Normaliza a resposta da API para { data, total } esperado pela UI */
function normalizeGroupsResponse(
  raw: UserGroupPaginationApiResponse
): FetchGroupsResponse {
  const data = Array.isArray(raw.userGroups) ? raw.userGroups : [];
  const total =
    typeof raw.totalRecords === 'number' ? raw.totalRecords : data.length;

  return {
    data: data.map(item => ({
      id: item.id,
      name: item.name,
      assignedCourses: item.assignedCourses,
      users: item.qtdUsers,
    })),
    total,
  };
}

export type FetchGroupsFn = (
  params: FetchGroupsParams
) => Promise<FetchGroupsResponse>;

const USERS_FOR_GROUP_SELECTION_PAGE_SIZE = 3;

/**
 * Cria o cliente de API de grupos.
 * Recebe o api do app (b2b/b2b2c) e retorna { fetchGroups, fetchUsersForGroupSelection }.
 */
export function createGroupsApi(api: GroupsApiClient) {
  return {
    /**
     * Lista grupos de usuário (GET v1/UserGroup/pagination).
     * Usa Name como filtro de busca e paginação server-side.
     */
    async fetchGroups(params: FetchGroupsParams): Promise<FetchGroupsResponse> {
      const { page, limit, search } = params;
      const searchParams = new URLSearchParams();
      searchParams.set('PageNumber', String(page));
      searchParams.set('PageSize', String(limit));
      searchParams.set('includeInactive', String(false));
      if (search) searchParams.set('Name', search);

      const query = searchParams.toString();
      const url = query
        ? `/${USER_GROUP_PAGINATION_PATH}?${query}`
        : `/${USER_GROUP_PAGINATION_PATH}`;

      try {
        const raw = await api.get<UserGroupPaginationApiResponse>(url);
        return normalizeGroupsResponse(raw);
      } catch {
        return {
          data: [],
          total: 0,
        };
      }
    },

    /**
     * Lista usuários para seleção no modal de grupos (GET v1/User/pagination).
     * PageSize fixo 3 e includeInactive=false.
     */
    async fetchUsersForGroupSelection(
      params: FetchUsersForGroupSelectionParams
    ): Promise<FetchUsersForGroupSelectionResponse> {
      const { page, search } = params;
      const searchParams = new URLSearchParams();
      searchParams.set('PageNumber', String(page));
      searchParams.set('PageSize', String(USERS_FOR_GROUP_SELECTION_PAGE_SIZE));
      searchParams.set('includeInactive', String(false));
      if (search) searchParams.set('SearchText', search);

      const query = searchParams.toString();
      const url = query
        ? `/${USER_PAGINATION_PATH}?${query}`
        : `/${USER_PAGINATION_PATH}`;

      try {
        const raw = await api.get<UserPaginationApiResponse>(url);
        const data = Array.isArray(raw.users) ? raw.users : [];
        const total =
          typeof raw.totalRecords === 'number' ? raw.totalRecords : data.length;
        return {
          data: data.map(u => ({
            id: u.id,
            name: u.name ?? u.email,
            email: u.email,
          })),
          total,
        };
      } catch {
        return { data: [], total: 0 };
      }
    },

    /**
     * Cria um novo grupo (POST v1/UserGroup).
     * Retorna o grupo criado com id para uso em addUsersToGroup.
     */
    async createGroup(
      body: CreateUserGroupRequest
    ): Promise<CreateUserGroupResponse> {
      return api.post<CreateUserGroupResponse>(
        `/${USER_GROUP_BASE_PATH}`,
        body
      );
    },

    /**
     * Adiciona usuários a um grupo (POST v1/UserGroup/groups/{groupId}/users).
     */
    async addUsersToGroup(
      groupId: string,
      body: AddUsersToGroupRequest
    ): Promise<void> {
      await api.post<void>(
        `/${USER_GROUP_BASE_PATH}/groups/${encodeURIComponent(groupId)}/users`,
        body
      );
    },

    /**
     * Busca um grupo por ID (GET v1/UserGroup/{groupId}).
     */
    async getGroup(groupId: string): Promise<UserGroupDetail> {
      return api.get<UserGroupDetail>(
        `/${USER_GROUP_BASE_PATH}/${encodeURIComponent(groupId)}`
      );
    },

    /**
     * Atualiza um grupo (PUT v1/UserGroup/{groupId}).
     */
    async updateGroup(
      groupId: string,
      body: UpdateUserGroupRequest
    ): Promise<void> {
      await api.put<void>(
        `/${USER_GROUP_BASE_PATH}/${encodeURIComponent(groupId)}`,
        body
      );
    },

    /**
     * Exclui um grupo (DELETE v1/UserGroup/{groupId}).
     */
    async deleteGroup(groupId: string): Promise<void> {
      await api.delete<void>(
        `/${USER_GROUP_BASE_PATH}/${encodeURIComponent(groupId)}`
      );
    },
  };
}

export type GroupsApi = ReturnType<typeof createGroupsApi>;
